import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { ProTopBar } from '../components/ProTopBar';
import { useQuery } from '@tanstack/react-query';
import { getNutritionCatalog, type NutritionCatalogItem } from '../lib/api';
import styles from './Dashboard.module.css';

type SortKey = 'name' | 'kcal_per_100g';
type SortDir = 'asc' | 'desc';

const th: React.CSSProperties = {
  padding: '10px 12px',
  textAlign: 'left',
  fontFamily: 'var(--mono)',
  fontSize: 10.5,
  fontWeight: 700,
  color: 'var(--mute)',
  letterSpacing: 1.2,
  textTransform: 'uppercase',
  borderBottom: '1px solid var(--line)',
  background: 'var(--surface-2)',
  whiteSpace: 'nowrap',
  userSelect: 'none',
};

const thNum: React.CSSProperties = {
  ...th,
  textAlign: 'right',
};

const td: React.CSSProperties = {
  padding: '10px 12px',
  borderBottom: '1px solid var(--line)',
  fontSize: 13,
  color: 'var(--ink)',
  verticalAlign: 'middle',
};

const tdNum: React.CSSProperties = {
  ...td,
  textAlign: 'right',
  fontFamily: 'var(--mono)',
  fontVariantNumeric: 'tabular-nums',
  color: 'var(--ink-2)',
};

function SortIndicator({ active, dir }: { active: boolean; dir: SortDir }) {
  if (!active) return <span style={{ opacity: 0.25, marginLeft: 4 }}>↕</span>;
  return <span style={{ marginLeft: 4 }}>{dir === 'asc' ? '▲' : '▼'}</span>;
}

export default function Catalogo() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['nutrition-catalog'],
    queryFn: getNutritionCatalog,
    staleTime: Infinity,
  });

  const [searchQ, setSearchQ] = useState('');
  const [category, setCategory] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const categories = useMemo<string[]>(() => {
    if (!data) return [];
    const set = new Set<string>();
    for (const item of data.results) {
      if (item.category != null) set.add(item.category);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'it'));
  }, [data]);

  const filtered = useMemo<NutritionCatalogItem[]>(() => {
    if (!data) return [];
    const q = searchQ.trim().toLowerCase();
    let list = data.results.filter((item) => {
      if (category && item.category !== category) return false;
      if (q && !item.name.toLowerCase().includes(q)) return false;
      return true;
    });
    list = [...list].sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'name') {
        cmp = a.name.localeCompare(b.name, 'it');
      } else {
        cmp = a.kcal_per_100g - b.kcal_per_100g;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [data, searchQ, category, sortKey, sortDir]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const totalCount = data ? data.results.length : 0;

  return (
    <>
      {/* Top bar: nessuna CTA (catalogo sola consultazione) */}
      <ProTopBar />

      {/* Header manuale — mirror di Piani.tsx */}
      <div style={{ padding: '4px 0 18px' }}>
        <div
          style={{
            fontFamily: 'var(--mono)',
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: 1.6,
            color: 'var(--mute)',
            textTransform: 'uppercase',
          }}
        >
          Studio Pro · Catalogo
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 16,
            flexWrap: 'wrap',
          }}
        >
          <div>
            <h1
              style={{
                fontFamily: 'var(--display)',
                fontWeight: 800,
                fontSize: 32,
                letterSpacing: -1,
                margin: '6px 0 0',
                lineHeight: 1.05,
              }}
            >
              Catalogo cibi
            </h1>
            {data && (
              <div
                style={{
                  fontSize: 13,
                  color: 'var(--mute)',
                  marginTop: 5,
                  fontFamily: 'var(--sans)',
                }}
              >
                {totalCount} alimenti CREA
              </div>
            )}
          </div>
        </div>
      </div>

      {/* KPI cards */}
      <div className={styles.kpis}>
        <div className={styles.kpi}>
          <div className={styles.kpiLabel}>Alimenti totali</div>
          <div className={`${styles.kpiVal} tnum`}>{isLoading ? '…' : totalCount}</div>
        </div>
        <div className={styles.kpi}>
          <div className={styles.kpiLabel}>Categorie</div>
          <div className={`${styles.kpiVal} tnum`}>{isLoading ? '…' : categories.length}</div>
        </div>
        <div className={styles.kpi}>
          <div className={styles.kpiLabel}>Risultati</div>
          <div className={`${styles.kpiVal} tnum`}>{isLoading ? '…' : filtered.length}</div>
        </div>
      </div>

      {/* Filtri */}
      <section className={styles.card} style={{ padding: 16, marginBottom: 14 }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 10,
            alignItems: 'end',
          }}
        >
          {/* Ricerca */}
          <div style={{ position: 'relative' }}>
            <Search
              size={14}
              style={{
                position: 'absolute',
                top: 11,
                left: 10,
                color: 'var(--mute)',
                pointerEvents: 'none',
              }}
            />
            <input
              type="text"
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              placeholder="Cerca alimento…"
              style={{
                width: '100%',
                border: '1px solid var(--line)',
                borderRadius: 6,
                padding: '8px 10px 8px 30px',
                fontSize: 13,
                background: 'var(--surface)',
                color: 'var(--ink)',
                fontFamily: 'var(--sans)',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Filtro categoria */}
          <div>
            <label
              style={{
                display: 'block',
                fontFamily: 'var(--mono)',
                fontSize: 10,
                fontWeight: 700,
                color: 'var(--mute)',
                letterSpacing: 1.2,
                textTransform: 'uppercase',
                marginBottom: 4,
              }}
            >
              Categoria
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              style={{
                width: '100%',
                border: '1px solid var(--line)',
                borderRadius: 6,
                padding: '8px 10px',
                fontSize: 13,
                background: 'var(--surface)',
                color: 'var(--ink)',
                boxSizing: 'border-box',
              }}
            >
              <option value="">Tutte le categorie</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {/* Tabella */}
      <section className={styles.card}>
        {isLoading ? (
          <div className={styles.empty}>Caricamento catalogo…</div>
        ) : isError ? (
          <div className={styles.empty} style={{ color: 'var(--red)' }}>
            Impossibile caricare il catalogo.
          </div>
        ) : filtered.length === 0 ? (
          <div className={styles.empty}>Nessun alimento per questi filtri.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  {/* NOME — ordinabile */}
                  <th
                    style={{ ...th, cursor: 'pointer' }}
                    onClick={() => handleSort('name')}
                  >
                    Nome
                    <SortIndicator active={sortKey === 'name'} dir={sortDir} />
                  </th>
                  <th style={th}>Categoria</th>
                  {/* KCAL/100g — ordinabile */}
                  <th
                    style={{ ...thNum, cursor: 'pointer' }}
                    onClick={() => handleSort('kcal_per_100g')}
                  >
                    Kcal/100g
                    <SortIndicator active={sortKey === 'kcal_per_100g'} dir={sortDir} />
                  </th>
                  <th style={thNum}>Prot</th>
                  <th style={thNum}>Carb</th>
                  <th style={thNum}>Grassi</th>
                  <th style={thNum}>Fibre</th>
                  <th style={thNum}>Porzione</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((item) => (
                  <tr
                    key={item.food_code}
                    style={{ cursor: 'default' }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLTableRowElement).style.background =
                        'var(--surface-2)';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLTableRowElement).style.background = '';
                    }}
                  >
                    <td style={td}>
                      <div
                        style={{
                          fontWeight: 600,
                          color: 'var(--ink)',
                          fontFamily: 'var(--sans)',
                        }}
                      >
                        {item.name}
                      </div>
                    </td>
                    <td style={td}>
                      {item.category != null ? (
                        <span
                          style={{
                            fontSize: 11.5,
                            color: 'var(--mute)',
                            fontFamily: 'var(--sans)',
                          }}
                        >
                          {item.category}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--mute)' }}>—</span>
                      )}
                    </td>
                    <td style={tdNum}>
                      {Math.round(item.kcal_per_100g)}
                    </td>
                    <td style={tdNum}>
                      {item.proteins_per_100g != null
                        ? item.proteins_per_100g.toFixed(1)
                        : '—'}
                    </td>
                    <td style={tdNum}>
                      {item.carbs_per_100g != null
                        ? item.carbs_per_100g.toFixed(1)
                        : '—'}
                    </td>
                    <td style={tdNum}>
                      {item.fats_per_100g != null
                        ? item.fats_per_100g.toFixed(1)
                        : '—'}
                    </td>
                    <td style={tdNum}>
                      {item.fiber_per_100g != null
                        ? item.fiber_per_100g.toFixed(1)
                        : '—'}
                    </td>
                    <td style={tdNum}>
                      {item.portion_g != null ? `${item.portion_g} g` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
